import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { OpeningBalance, Party } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { inrToKinrString, inrToKinrPreview } from '../money/inr';

export function StepOpeningBalances() {
  const qc = useQueryClient();
  const parties = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });
  const balances = useQuery<OpeningBalance[]>({
    queryKey: ['opening-balances'],
    queryFn: () => api.get<OpeningBalance[]>('/api/opening-balances'),
  });

  const [form, setForm] = useState({
    partyId: '',
    asOfDate: '',
    amount: '',
    note: '',
    source: 'TRADE_TIME_CARRYOVER',
  });
  const [error, setError] = useState<string | null>(null);

  const upsertMut = useMutation({
    mutationFn: async () => {
      if (!form.partyId) throw new Error('Pick a party.');
      if (!form.asOfDate) throw new Error('As-of date is required.');
      if (!form.amount.trim()) throw new Error('Amount is required.');
      if (!form.note.trim()) throw new Error('Note is required.');
      const kinrAmount = inrToKinrString(form.amount);
      if (kinrAmount === null) throw new Error('Amount is not a valid number.');
      return api.put<OpeningBalance>('/api/opening-balances', {
        partyId: Number(form.partyId),
        asOfDate: form.asOfDate,
        amount: kinrAmount,
        note: form.note.trim(),
        source: form.source,
      });
    },
    onSuccess: () => {
      setForm({ ...form, amount: '', note: '' });
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/opening-balances/${id}`),
    onSuccess: () => void qc.invalidateQueries(),
  });

  const nonSelfParties = (parties.data ?? []).filter((p) => p.role !== 'self');
  const partyById = new Map(nonSelfParties.map((p) => [p.id, p.displayName]));

  return (
    <div className="space-y-6">
      <Card>
        <header className="mb-4">
          <h2 className="text-lg font-semibold">Opening balances</h2>
          <p className="mt-1 text-caption text-ink-muted">
            For each party (clients + upline), enter a single opening balance
            as of your chosen start date. Positive = they owe you; negative =
            you owe them. Enter amounts in rupees — they're converted to
            K-INR internally (preview shown below the input). A required
            note captures provenance (usually the old TRADE-TIME ledger).
          </p>
        </header>

        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            upsertMut.mutate();
          }}
        >
          <Select
            label="Party"
            value={form.partyId}
            onChange={(v) => setForm({ ...form, partyId: v })}
            options={nonSelfParties.map((p) => ({
              value: String(p.id),
              label: `${p.displayName} (${p.role})`,
            }))}
            placeholder="Choose…"
          />
          <TextInput
            label="As-of date"
            type="date"
            value={form.asOfDate}
            onChange={(e) => setForm({ ...form, asOfDate: e.target.value })}
          />
          <div className="flex flex-col">
            <TextInput
              label="Amount (₹ INR)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="e.g. -192024 (they're owed ₹192,024)"
              inputMode="decimal"
              hint="Positive = party owes you; negative = you owe party. Enter in rupees."
            />
            {form.amount.trim().length > 0 && (
              <p className={`mt-1 text-caption ${
                inrToKinrPreview(form.amount) ? 'text-pos' : 'text-neg'
              }`}>
                {inrToKinrPreview(form.amount) ?? 'Not a valid amount'}
                {inrToKinrPreview(form.amount) && (
                  <span className="ml-1 text-ink-muted">(stored value)</span>
                )}
              </p>
            )}
          </div>
          <TextInput
            label="Source"
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            hint="Where this number came from."
          />
          <div className="col-span-full">
            <TextArea
              label="Note"
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. carried from TRADE-TIME Gori ledger CL.Bal"
            />
          </div>
          {error && (
            <div className="col-span-full">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}
          <div className="col-span-full">
            <Button type="submit" disabled={upsertMut.isPending}>
              {upsertMut.isPending ? <Spinner /> : 'Save opening balance'}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <h3 className="mb-3 text-small font-medium text-ink">
          Recorded opening balances
        </h3>
        {balances.isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading…
          </div>
        ) : balances.data && balances.data.length > 0 ? (
          <ul className="divide-y divide-border rounded-md border border-border">
            {balances.data.map((b) => (
              <li
                key={b.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-medium">
                    {partyById.get(b.partyId) ?? `#${b.partyId}`}
                  </span>
                  <span className="ml-2 text-ink-muted">
                    {b.asOfDate} · {b.amount} K · {b.source}
                  </span>
                  <p className="text-caption text-ink-muted">{b.note}</p>
                </div>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm('Delete this opening balance?')) deleteMut.mutate(b.id);
                  }}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-caption text-ink-muted">No opening balances yet.</p>
        )}
      </Card>
    </div>
  );
}
